import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Page } from './HostRoomBooking';

export const AUDIT_EVENT_TYPES = [
  'ADD_SPACE_RANK_BOOST',
  'ADD_USER_MODERATION',
  'APPROVE_BOOKING',
  'CANCEL_BOOKING',
  'CONFIRM_BOOKING',
  'CREATE_BOOKING',
  'CREATE_FAKE_USER',
  'CREATE_REVIEW',
  'DELETE_FAKE_USER',
  'DELETE_USER',
  'DISABLE_ORGANIZATION',
  'ENABLE_ORGANIZATION',
  'PROPOSE_BOOKING',
  'REFUND_BOOKING',
  'REJECT_BOOKING',
  'REMOVE_SPACE_RANK_BOOST',
  'REMOVE_USER_MODERATION',
  'UPDATE_BOOKING',
  'UPDATE_ORGANIZATION',
  'UPDATE_ORGANIZATION_STUDENT',
  'UPDATE_USER',
] as const;

export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];

export const AUDIT_OPERATION_TYPES = ['READ', 'WRITE'] as const;

export type AuditOperationType = (typeof AUDIT_OPERATION_TYPES)[number];

export const AUDIT_ACTOR_ROLES = ['USER', 'HOST', 'ADMIN', 'TUTOR'] as const;

export type AuditActorRole = (typeof AUDIT_ACTOR_ROLES)[number];

export type AdminAuditLogResponse = {
  id: string;
  eventType: AuditEventType;
  operationType: AuditOperationType;
  targetId: string;
  actorUserId: string | null;
  actorName: string | null;
  actorRole: AuditActorRole | null;
  organizationId: string | null;
  context: Record<string, unknown>;
  createdAt: string;
};

export type AdminAuditLogPage = Page<AdminAuditLogResponse>;

export type AdminAuditLogQuery = {
  eventType?: AuditEventType;
  operationType?: AuditOperationType;
  actorUserId?: string;
  targetId?: string;
  organizationId?: string;
  createdFrom?: string;
  createdTo?: string;
  page?: number;
  size?: number;
};

export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** IANA names for the audit-log display timezone picker. */
export function listIanaTimeZones(): string[] {
  const zones = new Set<string>(['UTC', getBrowserTimeZone()]);
  try {
    const supported = Intl.supportedValuesOf?.('timeZone');
    if (supported) {
      for (const zone of supported) {
        if (zone) zones.add(zone);
      }
    }
  } catch {
    // Intl.supportedValuesOf is missing or rejected the key.
  }
  return Array.from(zones).sort((a, b) => a.localeCompare(b));
}

export function formatTimeZoneOptionLabel(timeZone: string, now: Date = new Date()): string {
  try {
    const offset = formatInTimeZone(now, timeZone, 'xxx');
    return `${timeZone} (UTC${offset})`;
  } catch {
    return timeZone;
  }
}

/** Parse audit `createdAt` as a UTC instant (API LocalDateTime has no offset). */
export function parseAuditCreatedAt(value: string): Date {
  const trimmed = value.trim();
  if (!trimmed) {
    return new Date(Number.NaN);
  }
  return new Date(
    trimmed.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed) ? trimmed : `${trimmed}Z`
  );
}

export function formatAuditCreatedAt(value: string, timeZone: string): string {
  try {
    const instant = parseAuditCreatedAt(value);
    if (Number.isNaN(instant.getTime())) {
      return value;
    }
    return formatInTimeZone(instant, timeZone, 'MMM d · HH:mm');
  } catch {
    return value;
  }
}

export function formatAuditCreatedAtFull(value: string, timeZone: string): string {
  try {
    const instant = parseAuditCreatedAt(value);
    if (Number.isNaN(instant.getTime())) {
      return value;
    }
    return formatInTimeZone(instant, timeZone, 'yyyy-MM-dd HH:mm:ss zzz');
  } catch {
    return value;
  }
}

/** Normalize datetime-local values to ISO local datetime (`YYYY-MM-DDTHH:mm:ss`). */
export function toAuditLocalDateTime(value: string | undefined | null): string | undefined {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00`;
  }
  return trimmed;
}

export function buildAdminAuditLogParams(query: AdminAuditLogQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (query.eventType) params.eventType = query.eventType;
  if (query.operationType) params.operationType = query.operationType;
  if (query.actorUserId?.trim()) params.actorUserId = query.actorUserId.trim();
  if (query.targetId?.trim()) params.targetId = query.targetId.trim();
  if (query.organizationId?.trim()) params.organizationId = query.organizationId.trim();
  const createdFrom = toAuditLocalDateTime(query.createdFrom);
  if (createdFrom) params.createdFrom = createdFrom;
  const createdTo = toAuditLocalDateTime(query.createdTo);
  if (createdTo) params.createdTo = createdTo;
  if (query.page !== undefined) params.page = query.page;
  if (query.size !== undefined) params.size = query.size;
  return params;
}

function contextString(context: Record<string, unknown>, key: string): string | undefined {
  const value = context[key];
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return undefined;
}

/** Prefer short local display for booking start/end snapshots. */
export function formatAuditContextTimeRange(context: Record<string, unknown>): string | undefined {
  const start = contextString(context, 'startTime');
  const end = contextString(context, 'endTime');
  if (!start && !end) {
    return undefined;
  }

  const formatPart = (value: string, withDate: boolean): string => {
    try {
      return format(parseISO(value), withDate ? 'MMM d, HH:mm' : 'HH:mm');
    } catch {
      return value;
    }
  };

  if (start && end) {
    return `${formatPart(start, true)}–${formatPart(end, false)}`;
  }
  return formatPart(start ?? end!, true);
}

export type AuditRowDisplayLabels = {
  unknownActor: string;
  globalScope: string;
  missingOrganization: string;
};

export type AuditRowDisplay = {
  actor: string;
  organization: string;
  /** Remaining context (booker/space/room/time/…), excluding actor and organization. */
  details: string;
};

/**
 * Column-oriented list labels from actor + known display-name context keys.
 * Does not invent missing fields or resolve UUIDs.
 */
export function formatAuditRowDisplay(
  log: AdminAuditLogResponse,
  labels: AuditRowDisplayLabels
): AuditRowDisplay {
  const context = log.context ?? {};
  const parts: string[] = [];
  const push = (value: string | undefined) => {
    if (!value) return;
    if (!parts.includes(value)) {
      parts.push(value);
    }
  };

  const bookerName = contextString(context, 'bookerName');
  const tutorName = contextString(context, 'tutorName');
  const organizationName = contextString(context, 'organizationName');
  const spaceName = contextString(context, 'spaceName');
  const roomName = contextString(context, 'roomName');
  const name = contextString(context, 'name');
  const bandName = contextString(context, 'bandName');
  const bookingKind = contextString(context, 'bookingKind');

  push(bookerName);
  push(tutorName);
  push(spaceName);
  push(roomName);

  // Non-booking writers (users, fake users, orgs, …)
  if (!bookingKind) {
    push(name);
    push(bandName);
  }

  push(formatAuditContextTimeRange(context));

  let organization: string;
  if (organizationName) {
    organization = organizationName;
  } else if (!log.organizationId) {
    organization = labels.globalScope;
  } else {
    organization = labels.missingOrganization;
  }

  return {
    actor: log.actorName ?? labels.unknownActor,
    organization,
    details: parts.join(' · '),
  };
}

/** Flat secondary line kept for callers that want a single string. */
export function formatAuditRowSummary(
  log: AdminAuditLogResponse,
  labels: AuditRowDisplayLabels
): string {
  const { actor, organization, details } = formatAuditRowDisplay(log, labels);
  return [actor, details, organization].filter(Boolean).join(' · ');
}
